/**
 * MentionText — renders plain post/comment/message text with URLs, @mentions,
 * and #hashtags as clickable links. Mentions open the member's profile;
 * hashtags open the feed filtered by that tag.
 *
 * The API keeps mentions inside the body text, so each label is matched once,
 * here, against the people known for this context (`mentions`). The resolved
 * identity travels with the segment and drives navigation, so clicking never
 * depends on the label still matching a username.
 */
import { type MouseEvent, useMemo } from 'react';
import { Hashtag } from './Hashtag';
import { useMentionNavigate } from './richTextNavigation';
import {
  UNRESOLVED_MENTION,
  resolveMention,
  type MentionIndex,
  type MentionMatch,
} from '@wlb/shared/utils/mentions';
import './richText.css';

interface MentionTextProps {
  text: string;
  /** Identities known for this post/comment/thread, from `buildMentionIndex`. */
  mentions?: MentionIndex;
}

type Segment =
  | { type: 'text'; value: string }
  | { type: 'mention'; value: string; match: MentionMatch }
  | { type: 'hashtag'; value: string }
  | { type: 'url'; value: string; href: string };

/**
 * Splits text into segments: plain text, URLs, @username mentions, and
 * #hashtags.
 * The regex matches both patterns in a single pass so they interleave
 * correctly in the output.
 */
export function parseRichText(text: string, mentions?: MentionIndex): Segment[] {
  // Matches HTTP(S) URLs, www URLs, @username, or #hashtag.
  const regex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)|(@[a-zA-Z0-9_.]+)|(#[\p{L}\p{N}_]+)/giu;
  const segments: Segment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    if (match[1]) {
      const trailingPunctuation = match[1].match(/[.,!?;:'")\]}]+$/)?.[0] ?? '';
      const value = match[1].slice(0, match[1].length - trailingPunctuation.length);
      segments.push({
        type: 'url',
        value,
        href: /^www\./i.test(value) ? `https://${value}` : value,
      });
      if (trailingPunctuation) {
        segments.push({ type: 'text', value: trailingPunctuation });
      }
    } else if (match[2]) {
      const label = match[2].slice(1);
      segments.push({
        type: 'mention',
        value: label,
        match: mentions ? resolveMention(mentions, label) : UNRESOLVED_MENTION,
      });
    } else if (match[3]) {
      // #hashtag — keep the full #tag
      segments.push({ type: 'hashtag', value: match[3] });
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return segments;
}

export function MentionText({ text, mentions }: MentionTextProps) {
  const navigateToMention = useMentionNavigate();

  const segments = useMemo(() => parseRichText(text, mentions), [text, mentions]);

  const handleMentionClick = (
    match: MentionMatch,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    navigateToMention(match);
  };

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === 'mention') {
          const { userGuid, ambiguous, displayName } = segment.match;
          // Only a uniquely resolved mention is clickable; ambiguous or
          // unknown labels stay as plain text so they never navigate wrongly.
          if (!userGuid || ambiguous) {
            return <span key={index} className="mention-label">@{segment.value}</span>;
          }
          const label = displayName || segment.value;
          return (
            <button
              key={index}
              type="button"
              className="mention-link"
              onClick={event => handleMentionClick(segment.match, event)}
            >
              @{label}
            </button>
          );
        }
        if (segment.type === 'hashtag') {
          return <Hashtag key={index}>{segment.value}</Hashtag>;
        }
        if (segment.type === 'url') {
          return (
            <a
              key={index}
              className="text-link"
              href={segment.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={event => event.stopPropagation()}
            >
              {segment.value}
            </a>
          );
        }
        return <span key={index}>{segment.value}</span>;
      })}
    </>
  );
}
